import { Node, mergeAttributes } from "@tiptap/core";

export interface ResearchImageOptions {
  HTMLAttributes: Record<string, unknown>;
  resolveSrc?: (assetId: string) => string | null;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    researchImage: {
      insertResearchImage: (attrs: { assetId: string; src?: string; alt?: string }) => ReturnType;
    };
  }
}

export const ResearchImage = Node.create<ResearchImageOptions>({
  name: "image",
  group: "block",
  atom: true,
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      resolveSrc: undefined,
    };
  },

  addAttributes() {
    return {
      id: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-paragraph-id") || element.getAttribute("id"),
        renderHTML: (attributes) => {
          if (!attributes.id) return {};
          return { "data-paragraph-id": attributes.id };
        },
      },
      assetId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-asset-id"),
        renderHTML: (attributes) => {
          if (!attributes.assetId) return {};
          return { "data-asset-id": attributes.assetId };
        },
      },
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => {
          if (!attributes.src) return {};
          return { src: attributes.src };
        },
      },
      alt: {
        default: "",
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) => (attributes.alt ? { alt: attributes.alt } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "img[data-asset-id]" }, { tag: "img[src]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const assetId = typeof node.attrs.assetId === "string" ? node.attrs.assetId : "";
    const resolved = assetId && this.options.resolveSrc ? this.options.resolveSrc(assetId) : null;
    const src = resolved || node.attrs.src || "";
    return [
      "img",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        src,
        class: "manuscript-research-image",
        style: "max-width: 100%; height: auto; display: block; margin: 0.75rem auto;",
      }),
    ];
  },

  addCommands() {
    return {
      insertResearchImage:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              id: attrs.assetId ? `img-${attrs.assetId}` : "",
              assetId: attrs.assetId,
              src: attrs.src || "",
              alt: attrs.alt || "",
            },
          }),
    };
  },
});
